<template>
	<div class="c-global-checkout__form-section"
		 :class="{'c-global-checkout__form-section--reverse' : showCityFormFieldFirst}">

		<div class="c-global-checkout__inline-fields">
			<template v-for="field in orderedFields" :key="field.name">
				<TwoInputField
					v-bind="field.props"
					:is="componentMap[field.component]"
					:model-value="formData[field.name]"
					@update:model-value="emitUpdateForm(field.name, $event)"
					@blur="onBlur"
					@focus="onFocus"
				/>
			</template>

		</div>
	</div>
</template>

<script setup>
	import { computed } from 'vue';
	import TwoInputField from './twoInputField.vue';

	const props = defineProps({
		enableAddressLookup: {
			type: Boolean,
			required: true,
		},
		cannotFindAddressLabel: {
			type: String,
			require: true,
		},
		formData: {
			type: Object,
			required: true,
		},
		submitted: Boolean,
		errors: {
			type: Object,
			default: () => {},
		},
		updateFieldError: {
			type: Function,
		},
		leftFieldId: {
			type: String,
			default: null,
		},
		leftLabel: {
			type: String,
			default: null,
		},
		leftName: {
			type: String,
			default: null,
		},
		leftFieldName: {
			type: String,
			default: null,
		},
		leftClientSideInputType: {
			type: String,
			default: 'text',
		},
		leftRequired: {
			type: Boolean,
			default: false,
		},
		leftMaxLength: {
			type: Number,
			default: 0,
		},
		leftMaxLengthErrorText: {
			type: String,
			default: '',
		},
		leftRequiredErrorText: {
			type: String,
			default: '',
		},
		leftPlaceholderText: {
			type: String,
			default: '',
		},
		rightPlaceholderText: {
			type: String,
			default: '',
		},
		rightFieldId: {
			type: String,
			default: null,
		},
		rightClientSideInputType: {
			type: String,
			default: 'text',
		},
		rightLabel: {
			type: String,
			default: null,
		},
		rightName: {
			type: String,
			default: null,
		},
		rightFieldName: {
			type: String,
			default: null,
		},
		rightRequired: {
			type: Boolean,
			default: false,
		},
		rightMaxLength: {
			type: Number,
			default: 0,
		},
		rightMaxLengthErrorText: {
			type: String,
			default: '',
		},
		rightRequiredErrorText: {
			type: String,
			default: '',
		},
		leftValidatePattern: {
			default: null,
		},
		leftValidateAllowPatterns: {
			type: Array,
			default: () => [],
		},
		leftValidateNotAllowPatterns: {
			type: Array,
			default: () => [],
		},
		rightValidatePattern: {
			default: null,
		},
		rightValidationAllowPatterns: {
			type: Array,
			default: () => [],
		},
		rightValidateNotAllowPatterns: {
			type: Array,
			default: () => [],
		},
		leftFormatErrorText: {
			type: String,
			default: '',
		},
		rightFormatErrorText: {
			type: String,
			default: '',
		},
		readOnly: {
			type: Boolean,
			default: false,
		},
		maxSuggestions: {
			type: Number,
			default: 0,
		},
		showCityFormFieldFirst: {
			type: Boolean,
			default: false,
		}
	});

	const emit = defineEmits(['update-form', 'blur', 'focus']);

	const componentMap = {
		cityField: 'city-field',
		postalCodeField: 'postal-code-field'
	};

	const orderedFields = computed(() => {
		let fields = [
			{
				name: props.leftFieldName,
				component: "postalCodeField",
				props: postalCodeFieldProps.value,
			},
			{
				name: props.rightFieldName,
				component: "cityField",
				props: cityFieldProps.value,
			}
		];

		return props.showCityFormFieldFirst ? fields.reverse() : fields;
	});

	const postalCodeFieldProps = computed(() => ({
		enableAddressLookup: props.enableAddressLookup,
		cannotFindAddressLabel: props.cannotFindAddressLabel,
		formData: props.formData,
		submitted: props.submitted,
		errors: props.errors,
		updateFieldError: props.updateFieldError,
		fieldId: props.leftFieldId,
		label: props.leftLabel,
		name: props.leftName,
		fieldName: props.leftFieldName,
		clientSideInputType: props.leftClientSideInputType,
		required: props.leftRequired,
		maxLength: props.leftMaxLength,
		maxLengthErrorText: props.leftMaxLengthErrorText,
		requiredErrorText: props.leftRequiredErrorText,
		placeholderText: props.leftPlaceholderText,
		validatePattern: props.leftValidatePattern,
		validateAllowPatterns: props.leftValidateAllowPatterns,
		validateNotAllowPatterns: props.leftValidateNotAllowPatterns,
		formatErrorText: props.leftFormatErrorText,
		readOnly: props.readOnly,
		maxSuggestions: props.maxSuggestions,
	}));

	const cityFieldProps = computed(() => ({
		enableAddressLookup: props.enableAddressLookup,
		cannotFindAddressLabel: props.cannotFindAddressLabel,
		formData: props.formData,
		submitted: props.submitted,
		errors: props.errors,
		updateFieldError: props.updateFieldError,
		fieldId: props.rightFieldId,
		label: props.rightLabel,
		name: props.rightName,
		fieldName: props.rightFieldName,
		clientSideInputType: props.rightClientSideInputType,
		required: props.rightRequired,
		maxLength: props.rightMaxLength,
		maxLengthErrorText: props.rightMaxLengthErrorText,
		requiredErrorText: props.rightRequiredErrorText,
		placeholderText: props.rightPlaceholderText,
		validatePattern: props.rightValidatePattern,
		validateAllowPatterns: props.rightValidationAllowPatterns,
		validateNotAllowPatterns: props.rightValidateNotAllowPatterns,
		formatErrorText: props.rightFormatErrorText,
		readOnly: props.readOnly,
	}));

	function emitUpdateForm(fieldName, value) {
		emit('update-form', { fieldName, value });
	}

	function onBlur(event) {
		emit('blur', event);
	}

	function onFocus(event) {
		emit('focus', event);
	}
</script>
